import { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import PageFooter from '../components/PageFooter';
import ProductCartCounter from '../components/ProductCartCounter';
import CombinedCheckoutModal from '../components/CombinedCheckoutModal';
import SalonHeaderActions from '../components/SalonHeaderActions';
import { useSkinSession } from '../context/SkinSessionContext';
import { SESSION_APP_DATA_BASE } from '../config/sessionAppData';
import { getSessionIdFromUrl, getSessionId, pathWithSessionId } from '../utils/sessionManager';
import { saveSkinFlowState, getSkinFlowState } from '../utils/skinFlowState';
import { getApiBaseUrl, SALON_API_PREFIX } from '../utils/apiConfig';
import { sanitizeHtml, stripHtml } from '../utils/sanitizeHtml';
import { fetchWithTimeout } from '../utils/fetchWithTimeout';
import { persistProductCart, persistTreatmentCart } from '../utils/cartPersistence';
import '../styles/PageBackground.css';
import './ProductRecommendationsPage.css';

const DEFAULT_PRODUCT_SUGGEST_LIMIT = 4;
const PRODUCT_RECOMMENDATIONS_CACHE_PREFIX = 'salon_product_recommendations_';

const ProductRecommendationsPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { token, sessionLandingPath } = useSkinSession();

  const sessionIdFromState = location.state?.sessionId || null;
  const sessionIdFromUrl = getSessionIdFromUrl(location);
  const sessionIdFromStorage = getSessionId();
  const sessionId = sessionIdFromState || sessionIdFromUrl || sessionIdFromStorage || null;

  const effectiveState = useMemo(() => {
    if (
      location.state &&
      (location.state.skinResults || location.state.routineData || location.state.recommendations || location.state.treatmentCartItems != null)
    ) {
      return location.state;
    }
    if (!sessionId) return null;
    return getSkinFlowState(sessionId);
  }, [location.state, sessionId]);

  const userDataFromState = effectiveState?.userData || location.state?.userData || null;
  const capturedImageFromState = effectiveState?.capturedImage || location.state?.capturedImage || null;
  const skinResultsFromState = effectiveState?.skinResults || location.state?.skinResults || null;
  const routineDataFromState = effectiveState?.routineData || location.state?.routineData || null;
  const aqiDataFromState = effectiveState?.aqiData ?? location.state?.aqiData ?? null;
  const cartItemsFromState = effectiveState?.cartItems ?? location.state?.cartItems ?? null;
  const treatmentCartItemsFromState = effectiveState?.treatmentCartItems ?? location.state?.treatmentCartItems ?? null;
  const updatedCartFromCheckout = location.state?.updatedCartFromCheckout || false;
  const [cartItems, setCartItems] = useState(cartItemsFromState || location.state?.cartItems || {}); // Store cart items: { productId: { product, quantity } });
  const [treatmentCartItems, setTreatmentCartItems] = useState(
    () =>
      treatmentCartItemsFromState && typeof treatmentCartItemsFromState === 'object'
        ? treatmentCartItemsFromState
        : {}
  );

  // Persist full state (including cartItems) so it survives refresh
  useEffect(() => {
    if (!sessionId) return;
    const current = getSkinFlowState(sessionId) || {};
    const fromLocation = location.state && typeof location.state === 'object' ? location.state : {};
    const merged = {
      ...current,
      ...fromLocation,
      sessionId,
      cartItems,
      treatmentCartItems,
    };
    saveSkinFlowState(sessionId, merged);
  }, [sessionId, cartItems, treatmentCartItems, location.state]);
  const [recommendations, setRecommendations] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('morning');
  const [uspPopup, setUspPopup] = useState({ open: false, text: '', heading: '', position: { top: 0, left: 0 } });
  const [isCheckoutModalOpen, setIsCheckoutModalOpen] = useState(false);

  // Product suggestion config from session app-data (suggest-product returns flags + counts)
  const [productSuggestLimit, setProductSuggestLimit] = useState(null);
  const [productSuggestionEnabled, setProductSuggestionEnabled] = useState(true);
  const [morningCount, setMorningCount] = useState(null);
  const [eveningCount, setEveningCount] = useState(null);
  const [organizationId, setOrganizationId] = useState(null);
  const [isConfigLoaded, setIsConfigLoaded] = useState(false);

  useEffect(() => {
    if (!token) {
      setProductSuggestLimit(null);
      setProductSuggestionEnabled(true);
      setMorningCount(null);
      setEveningCount(null);
      setIsConfigLoaded(true);
      return;
    }
    let cancelled = false;
    fetchWithTimeout(`${SESSION_APP_DATA_BASE}/${encodeURIComponent(token)}/suggest-product`, { timeoutMs: 20000 })
      .then((res) => {
        if (!res.ok) throw new Error('Suggest product config unavailable');
        return res.json();
      })
      .then((data) => {
        if (cancelled) return;
        const product = String(data?.product_suggestion || '').trim().toLowerCase();
        const mCount = Number.isNaN(Number(data?.morning_count)) ? null : Number(data?.morning_count);
        const eCount = Number.isNaN(Number(data?.evening_count)) ? null : Number(data?.evening_count);
        setProductSuggestionEnabled(product === 'yes');
        setMorningCount(mCount != null && mCount > 0 ? mCount : null);
        setEveningCount(eCount != null && eCount > 0 ? eCount : null);

        const limit = data?.product_suggest_limit;
        const num = typeof limit === 'number' ? limit : parseInt(limit, 10);
        setProductSuggestLimit(Number.isNaN(num) || num < 1 ? null : num);

        const orgId = data?.organization_id;
        setOrganizationId(orgId != null && orgId !== '' ? String(orgId) : null);
        setIsConfigLoaded(true);
      })
      .catch(() => {
        if (!cancelled) {
          setProductSuggestLimit(null);
          setProductSuggestionEnabled(true);
          setMorningCount(null);
          setEveningCount(null);
          setIsConfigLoaded(true);
        }
      });
    return () => { cancelled = true; };
  }, [token]);

  // Restore cart state from navigation state when coming back from checkout
  useEffect(() => {
    if (updatedCartFromCheckout && cartItemsFromState) {
      setCartItems(cartItemsFromState);
    }
  }, [cartItemsFromState, updatedCartFromCheckout]);

  const API_BASE_URL = getApiBaseUrl() || process.env.REACT_APP_API_URL || '';

  const isVercelBlobUrl = (url) => {
    if (!url || typeof url !== 'string') return false;
    try {
      const u = new URL(url);
      const host = (u.hostname || '').toLowerCase();
      return (
        u.protocol === 'https:' &&
        (host === 'blob.vercel-storage.com' ||
          host.endsWith('.blob.vercel-storage.com') ||
          host === 'public.blob.vercel-storage.com' ||
          host.endsWith('.public.blob.vercel-storage.com'))
      );
    } catch {
      return false;
    }
  };

  const resolveProductImageUrl = (url) => {
    if (!url) return url;
    if (!isVercelBlobUrl(url)) return url;
    const base = (API_BASE_URL || '').replace(/\/$/, '');
    return `${base}${SALON_API_PREFIX}/blob-proxy?url=${encodeURIComponent(url)}`;
  };

  const toNumberOrNull = (v) => {
    if (v == null || v === '') return null;
    const n = Number(v);
    return Number.isNaN(n) ? null : n;
  };

  const hasProductDiscount = (p) => {
    const d = toNumberOrNull(p?.discount);
    const net = toNumberOrNull(p?.amount);
    const sale = toNumberOrNull(p?.sale_price);
    if (net == null) return false;
    if (d != null && d > 0 && sale != null && sale > 0 && sale < net) return true;
    if (net != null && sale != null && sale > 0 && sale < net) return true;
    return false;
  };

  const hasProductNetPrice = (p) => toNumberOrNull(p?.amount) != null;
  const hasProductAmount = (p) => toNumberOrNull(p?.amount) != null;

  const formatDiscountBadge = (p) => {
    const d = Number(p?.discount);
    if (Number.isNaN(d) || d <= 0) return null;
    if (d > 0 && d <= 1) return `${Math.round(d * 100)}% OFF`;
    if (d > 1 && d <= 100) return `${Math.round(d)}% OFF`;
    return `₹${Math.round(d)} OFF`;
  };

  const persistRecommendations = useCallback((data) => {
    if (!sessionId || !data || typeof data !== 'object') return;
    const cacheKey = `${PRODUCT_RECOMMENDATIONS_CACHE_PREFIX}${sessionId}`;
    try {
      localStorage.setItem(cacheKey, JSON.stringify(data));
    } catch (e) {
      // ignore cache write issues
    }
    const current = getSkinFlowState(sessionId) || {};
    const fromLocation = location.state && typeof location.state === 'object' ? location.state : {};
    saveSkinFlowState(sessionId, {
      ...current,
      ...fromLocation,
      sessionId,
      recommendations: data,
      cartItems,
      treatmentCartItems,
    });
  }, [sessionId, location.state, cartItems, treatmentCartItems]);

  useEffect(() => {
    if (!sessionId) return;
    const fromState = effectiveState?.recommendations ?? location.state?.recommendations ?? null;
    if (fromState && typeof fromState === 'object') {
      setRecommendations(fromState);
      setIsLoading(false);
      return;
    }
    try {
      const cached = localStorage.getItem(`${PRODUCT_RECOMMENDATIONS_CACHE_PREFIX}${sessionId}`);
      if (cached) {
        const parsed = JSON.parse(cached);
        if (parsed && typeof parsed === 'object') {
          setRecommendations(parsed);
          setIsLoading(false);
        }
      }
    } catch (e) {
      // ignore cache read issues
    }
  }, [sessionId, effectiveState?.recommendations, location.state?.recommendations]);

  const handleAddToCart = (product, quantity, productId) => {
    // Update cart state with product ID as key, storing both product data and quantity
    setCartItems(prev => {
      if (quantity === 0) {
        // Remove item from cart if quantity is 0
        const newCart = { ...prev };
        delete newCart[productId];
        return newCart;
      }
      return {
        ...prev,
        [productId]: {
          product: product,
          quantity: quantity
        }
      };
    });
  };

  const handleCheckout = () => {
    setIsCheckoutModalOpen(true);
  };

  const handleCloseCheckoutModal = () => {
    setIsCheckoutModalOpen(false);
  };

  const handleCartUpdate = (updatedCartItems) => {
    setCartItems(updatedCartItems);
  };

  const handleTreatmentCartUpdate = (updatedTreatmentCartItems) => {
    setTreatmentCartItems(updatedTreatmentCartItems || {});
  };

  const getTotalTreatmentCartItems = () =>
    Object.values(treatmentCartItems).reduce((sum, item) => sum + (item.quantity || 0), 0);

  const getTotalCombinedCartItems = () => getTotalCartItems() + getTotalTreatmentCartItems();

  // Persist cart to backend whenever cart changes (Add to Cart, +/- quantity, or modal update)
  const saveCartToBackend = useCallback((items) => {
    if (!API_BASE_URL || !sessionId) return;
    persistProductCart(API_BASE_URL, sessionId, token, items).then((r) => {
      if (r && !r.ok) console.warn('Could not save cart to backend');
    });
  }, [API_BASE_URL, sessionId, token]);

  const saveTreatmentCartToBackend = useCallback((items) => {
    if (!API_BASE_URL || !sessionId) return;
    persistTreatmentCart(API_BASE_URL, sessionId, token, items).then((r) => {
      if (r && !r.ok) console.warn('Could not save treatment cart to backend');
    });
  }, [API_BASE_URL, sessionId, token]);

  useEffect(() => {
    saveCartToBackend(cartItems);
  }, [cartItems, saveCartToBackend]);

  useEffect(() => {
    saveTreatmentCartToBackend(treatmentCartItems);
  }, [treatmentCartItems, saveTreatmentCartToBackend]);

  const handleProductLinkClick = useCallback((product, productUrl, e) => {
    if (e) e.stopPropagation();
    const numericProductId = Number(product?.id ?? product?.product_id);
    const productId = Number.isFinite(numericProductId) ? numericProductId : null;
    const baseUrl = (API_BASE_URL || '').replace(/\/$/, '');
    if (baseUrl && sessionId && productId) {
      fetchWithTimeout(`${baseUrl}${SALON_API_PREFIX}/record-viewed-product`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          skin_id: sessionId,
          session_id: sessionId,
          product_id: productId,
          ...(token && { app_session_token: token }),
        }),
        timeoutMs: 10000,
      }).catch(() => {});
    }
    if (productUrl) {
      window.open(productUrl, '_blank', 'noopener,noreferrer');
    }
  }, [API_BASE_URL, sessionId, token]);

  const getTotalCartItems = () => {
    return Object.values(cartItems).reduce((sum, item) => sum + (item.quantity || 0), 0);
  };

  // Recommended products depend on suggest-product config and counts.
  useEffect(() => {
    if (!isConfigLoaded) return;
    if (!productSuggestionEnabled) {
      setIsLoading(false);
      setRecommendations(null);
      return;
    }
    if (recommendations) {
      setIsLoading(false);
      return;
    }

    const fetchRecommendations = async () => {
      if (!skinResultsFromState?.skinScore) {
        setError('Missing required data');
        setIsLoading(false);
        return;
      }

      try {
        // First, try to fetch stored products from database
        if (sessionId) {
          try {
            const storedParams = new URLSearchParams();
            if (token) storedParams.set('app_session_token', token);
            if (organizationId) storedParams.set('organization_id', organizationId);
            const storedQuery = storedParams.toString() ? `?${storedParams.toString()}` : '';
            const storedResponse = await fetchWithTimeout(
              `${API_BASE_URL}${SALON_API_PREFIX}/get-stored-products/${sessionId}${storedQuery}`,
              { timeoutMs: 20000 }
            );
            if (storedResponse.ok) {
              const storedData = await storedResponse.json().catch(() => ({}));
              const morning = storedData.recommended_products?.morning || [];
              const evening = storedData.recommended_products?.evening || [];
              const hasStoredProducts = (Array.isArray(morning) && morning.length > 0) || (Array.isArray(evening) && evening.length > 0);
              if (storedData.success && storedData.recommended_products && hasStoredProducts) {
                const morningLimit = (typeof morningCount === 'number' && morningCount > 0)
                  ? morningCount
                  : (productSuggestLimit ?? DEFAULT_PRODUCT_SUGGEST_LIMIT);
                const eveningLimit = (typeof eveningCount === 'number' && eveningCount > 0)
                  ? eveningCount
                  : (productSuggestLimit ?? DEFAULT_PRODUCT_SUGGEST_LIMIT);
                if (storedData.recommended_products.morning) {
                  storedData.recommended_products.morning = storedData.recommended_products.morning.slice(0, morningLimit);
                }
                if (storedData.recommended_products.evening) {
                  storedData.recommended_products.evening = storedData.recommended_products.evening.slice(0, eveningLimit);
                }
                setRecommendations(storedData);
                persistRecommendations(storedData);
                setIsLoading(false);
                return; // Successfully loaded from database
              }
            }
          } catch (storedErr) {
            // If fetching stored products fails, continue to regenerate
            console.log('Could not fetch stored products, regenerating...', storedErr);
          }
        }

        // Regenerate products only if stored products not found
        const skinScore = skinResultsFromState.skinScore;
        const conditionScores = skinScore.condition_scores || {};
        const detectedConditions = (skinResultsFromState.detections || []).map(d => d.class_name || d.className).filter(Boolean);

        const userData = userDataFromState || {};
        const userSkinType = userData.skin_type != null && userData.skin_type !== '' ? userData.skin_type : null;
        const userAge = userData.age != null && userData.age !== '' ? Number(userData.age) : null;
        const productLimit = (typeof productSuggestLimit === 'number' && productSuggestLimit >= 1)
          ? productSuggestLimit
          : null;
        const morningLimit = (typeof morningCount === 'number' && morningCount > 0) ? morningCount : (productLimit || DEFAULT_PRODUCT_SUGGEST_LIMIT);
        const eveningLimit = (typeof eveningCount === 'number' && eveningCount > 0) ? eveningCount : (productLimit || DEFAULT_PRODUCT_SUGGEST_LIMIT);

        console.log('[ProductRecommendations] recommend-products payload:', {
          user_info: { skin_type: userSkinType, age: userAge, userData_keys: Object.keys(userData || {}) },
          detected_conditions: detectedConditions,
          session_id: sessionId,
          morning_count: morningLimit,
          evening_count: eveningLimit,
          product_suggest_limit: productLimit,
          organization_id: organizationId || undefined
        });

        const body = {
          session_id: sessionId,
          final_skin_score: skinScore.final_skin_score || skinScore.score || 0,
          skin_grade: skinScore.grade || 'Fair',
          condition_scores: conditionScores,
          detected_conditions: detectedConditions,
          app_session_token: token,
          morning_count: morningLimit,
          evening_count: eveningLimit,
          ...(productLimit != null ? { product_suggest_limit: productLimit } : {}),
          ...(organizationId ? { organization_id: organizationId } : {}),
          ...(userSkinType != null ? { user_skin_type: userSkinType } : {}),
          ...(userAge != null ? { user_age: userAge } : {})
        };
        const response = await fetchWithTimeout(`${API_BASE_URL}${SALON_API_PREFIX}/recommend-products`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(body),
          timeoutMs: 30000,
        });

        if (!response.ok) {
          throw new Error('Failed to fetch recommendations');
        }

        const data = await response.json();
        console.log('[ProductRecommendations] recommend-products response:', {
          success: data.success,
          morning_count: (data.recommended_products?.morning || []).length,
          evening_count: (data.recommended_products?.evening || []).length,
          total_products_recommended: data.total_products_recommended
        });
        if (data.success) {
          // Keep backend order stable and only cap by product_suggest_limit
          if (data.recommended_products) {
            if (data.recommended_products.morning) {
              data.recommended_products.morning = data.recommended_products.morning.slice(0, morningLimit);
            }
            if (data.recommended_products.evening) {
              data.recommended_products.evening = data.recommended_products.evening.slice(0, eveningLimit);
            }
          }

          setRecommendations(data);
          persistRecommendations(data);
        } else {
          throw new Error(data.error || 'Failed to get recommendations');
        }
      } catch (err) {
        setError(err.message || 'Failed to load product recommendations');
      } finally {
        setIsLoading(false);
      }
    };

    fetchRecommendations();
  }, [skinResultsFromState, userDataFromState, API_BASE_URL, sessionId, productSuggestLimit, token, organizationId, recommendations, persistRecommendations, eveningCount, morningCount, productSuggestionEnabled, isConfigLoaded]);

  const handleCloseUspPopup = () => {
    setUspPopup({ open: false, text: '', heading: '', position: { top: 0, left: 0 } });
  };

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (uspPopup.open && !e.target.closest('.product-usp-popup') && !e.target.closest('.product-usp')) {
        handleCloseUspPopup();
      }
    };

    if (uspPopup.open) {
      document.addEventListener('click', handleClickOutside);
      return () => {
        document.removeEventListener('click', handleClickOutside);
      };
    }
  }, [uspPopup.open]);

  const handleBack = () => {
    const baseState = {
      capturedImage: capturedImageFromState,
      userData: userDataFromState,
      sessionId: sessionId,
      skinResults: skinResultsFromState,
      routineData: routineDataFromState,
      aqiData: aqiDataFromState,
      cartItems,
      treatmentCartItems,
      ...(token && { token }),
    };

    navigate(pathWithSessionId('/treatment-recommendations', sessionId), { state: baseState });
  };

  const handleHome = () => {
    navigate(sessionLandingPath);
  };

  return (
    <div className="product-recommendations-page-shell page-bg">
      <div className="product-recommendations-page-nav">
        <button
          type="button"
          className="product-recommendations-page-back-button"
          onClick={handleBack}
          aria-label="Go back"
        >
          ←
        </button>
        <div className="product-recommendations-page-title-wrapper">
          <SalonHeaderActions homeButtonClassName="product-recommendations-page-home-button" onHome={handleHome} />
          {getTotalCombinedCartItems() > 0 && (
            <button
              type="button"
              className="product-recommendations-page-checkout-button-icon"
              onClick={handleCheckout}
              title={`Checkout (${getTotalCombinedCartItems()} items)`}
              aria-label={`Checkout with ${getTotalCombinedCartItems()} items`}
            >
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="9" cy="21" r="1"></circle>
                <circle cx="20" cy="21" r="1"></circle>
                <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"></path>
              </svg>
              {getTotalCombinedCartItems() > 0 && (
                <span className="product-recommendations-page-checkout-badge-icon">
                  {getTotalCombinedCartItems()}
                </span>
              )}
            </button>
          )}
        </div>
      </div>
      {!productSuggestionEnabled ? (
        <main className="product-recommendations-page-main">
          <div className="product-recommendations-page-container">
            <div className="product-recommendations-page-error">
              <p>Product recommendations are not enabled for this session.</p>
              <button type="button" className="skin-action-button" onClick={handleBack}>
                Back
              </button>
            </div>
          </div>
        </main>
      ) : (
        <main className="product-recommendations-page-main">
          <div className="product-recommendations-page-container">
            <section className="product-recommendations-page-layout">

            {isLoading ? (
              <div className="product-recommendations-loading">
                <div className="loading-spinner"></div>
                <p>Loading personalized recommendations...</p>
              </div>
            ) : error ? (
              <div className="product-recommendations-error">
                <p>{error}</p>
                <button onClick={() => window.location.reload()}>Retry</button>
              </div>
            ) : recommendations ? (
              <>
                {(() => {
                  const baseLimit = (typeof productSuggestLimit === 'number' && productSuggestLimit >= 1) ? productSuggestLimit : null;
                  const finalMorningLimit = (typeof morningCount === 'number' && morningCount > 0) ? morningCount : (baseLimit || DEFAULT_PRODUCT_SUGGEST_LIMIT);
                  const finalEveningLimit = (typeof eveningCount === 'number' && eveningCount > 0) ? eveningCount : (baseLimit || DEFAULT_PRODUCT_SUGGEST_LIMIT);
                  const morningList = (recommendations.recommended_products.morning || []).slice(0, finalMorningLimit);
                  const eveningList = (recommendations.recommended_products.evening || []).slice(0, finalEveningLimit);
                  return (
                <div className="product-recommendations-content">
                  <div className="product-tabs">
                    <button
                      type="button"
                      className={`product-tab ${activeTab === 'morning' ? 'active' : ''}`}
                      onClick={() => setActiveTab('morning')}
                    >
                      <img
                        src={`${process.env.PUBLIC_URL}/icon/mor.png`}
                        alt="Morning"
                        className="product-tab-icon"
                      />
                      Morning ({morningList.length})
                    </button>
                    <button
                      type="button"
                      className={`product-tab ${activeTab === 'evening' ? 'active' : ''}`}
                      onClick={() => setActiveTab('evening')}
                    >
                      <img
                        src={`${process.env.PUBLIC_URL}/icon/eve.png`}
                        alt="Evening"
                        className="product-tab-icon"
                      />
                      Evening ({eveningList.length})
                    </button>
                  </div>

                  <div className="product-tab-panels">
                    {activeTab === 'morning' && (
                      <div className={`product-tab-panel ${morningList.length > 0 ? 'active' : ''}`}>
                        {morningList.length > 0 ? (
                          <div className="product-grid">
                            {morningList.map((product, idx) => {
                              const productId = product.product_id || product.id || `morning_${product.product_name}_${idx}`;
                              const cartQuantity = cartItems[productId]?.quantity || 0;
                              const showProductPrice = hasProductNetPrice(product);
                              const showProductQuantity = hasProductAmount(product);
                              const qtyForPrice = showProductQuantity ? Math.max(1, cartQuantity || 0) : 1;
                              const discountBadge = showProductPrice ? formatDiscountBadge(product) : null;
                              return (
                                <div key={productId} className="product-card">
                                  {discountBadge ? (
                                    <div className="product-discount-badge" aria-label={`Discount ${discountBadge}`}>
                                      {discountBadge}
                                    </div>
                                  ) : null}
                                  <div className="product-card-image">
                                    {product.image_url ? (
                                      <img
                                        src={resolveProductImageUrl(product.image_url)}
                                        alt={product.product_name}
                                        onError={(e) => {
                                          e.target.style.display = 'none';
                                        }}
                                      />
                                    ) : null}
                                  </div>
                                  <div className="product-info">
                                    <div className="product-name-header">
                                      {product.product_link ? (
                                        <h3 className="product-name product-name-link" onClick={(e) => handleProductLinkClick(product, product.product_link, e)} role="link" tabIndex={0} onKeyDown={(e) => { if (e.key === 'Enter') handleProductLinkClick(product, product.product_link, e); }}>
                                          {product.product_name}
                                        </h3>
                                      ) : (
                                        <h3 className="product-name">{product.product_name}</h3>
                                      )}
                                    </div>
                                    {(() => {
                                      const productTypeDisplay = product.product_type || product.productType || (Array.isArray(product.product_types) && product.product_types?.[0]) || product.product_types;
                                      return productTypeDisplay ? (
                                        <div className="product-type-heading">Type: {productTypeDisplay}</div>
                                      ) : null;
                                    })()}
                                    {product.major_usp && (() => {
                                      const majorUspPlain = stripHtml(product.major_usp);
                                      return (
                                        <div className="product-major-usp-wrapper">
                                          <span className="product-card-usp-heading">Key benefit</span>
                                          <span className="product-major-usp-text">{majorUspPlain}</span>
                                        </div>
                                      );
                                    })()}
                                    {product.concerns_treated && product.concerns_treated.length > 0 && (
                                      <div className="product-info-section">
                                        <div className="product-info-label">
                                          <img
                                            src={`${process.env.PUBLIC_URL}/icon/treat.png`}
                                            alt="Treats"
                                            className="product-info-icon"
                                          />
                                          <span>Treats</span>
                                        </div>
                                        <div className="product-tags-container">
                                          {product.concerns_treated.map((concern, i) => (
                                            <span key={i} className="product-tag">{concern}</span>
                                          ))}
                                        </div>
                                      </div>
                                    )}

                                    {product.skin_goals_supported && product.skin_goals_supported.length > 0 && (
                                      <div className="product-info-section">
                                        <div className="product-info-label">
                                          <img
                                            src={`${process.env.PUBLIC_URL}/icon/goal.png`}
                                            alt="Goals"
                                            className="product-info-icon"
                                          />
                                          <span>Goals</span>
                                        </div>
                                        <div className="product-tags-container">
                                          {product.skin_goals_supported.map((goal, i) => (
                                            <span key={i} className="product-tag">{goal}</span>
                                          ))}
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                  <div className="product-card-footer">
                                    {showProductPrice ? (
                                      <div className="product-price-quantity">
                                        {(() => {
                                          const unitNet = toNumberOrNull(product.amount) ?? 0;
                                          const unitSale = toNumberOrNull(product.sale_price) ?? unitNet;
                                          const totalNet = unitNet * qtyForPrice;
                                          const totalSale = unitSale * qtyForPrice;
                                          if (hasProductDiscount(product) && totalSale > 0 && totalSale < totalNet) {
                                            return (
                                              <>
                                                <span className="product-price product-net-price-slash">{`₹${totalNet.toFixed(0)}`}</span>
                                                <span className="product-price product-sale-price">{`₹${totalSale.toFixed(0)}`}</span>
                                              </>
                                            );
                                          }
                                          return (
                                            <span className="product-price">{`₹${totalNet.toFixed(0)}`}</span>
                                          );
                                        })()}
                                      </div>
                                    ) : null}
                                    {showProductQuantity ? (
                                      <ProductCartCounter
                                        key={`counter-${productId}`}
                                        product={product}
                                        onAddToCart={(product, quantity) => handleAddToCart(product, quantity, productId)}
                                        disabled={!product.add_to_cart}
                                        initialQuantity={cartQuantity}
                                      />
                                    ) : (
                                      <button
                                        type="button"
                                        className={`skin-action-button add-to-cart-button ${cartQuantity > 0 ? 'request-selected' : ''}`}
                                        onClick={() => handleAddToCart(product, cartQuantity > 0 ? 0 : 1, productId)}
                                      >
                                        {cartQuantity > 0 ? 'Selected for Stock' : 'Add to Stock Request'}
                                      </button>
                                    )}
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        ) : (
                          <div className="no-products-message">
                            <p>No morning products found.</p>
                          </div>
                        )}
                      </div>
                    )}

                    {activeTab === 'evening' && (
                      <div className={`product-tab-panel ${eveningList.length > 0 ? 'active' : ''}`}>
                        {eveningList.length > 0 ? (
                          <div className="product-grid">
                            {eveningList.map((product, idx) => {
                              const productId = product.product_id || product.id || `evening_${product.product_name}_${idx}`;
                              const cartQuantity = cartItems[productId]?.quantity || 0;
                              const showProductPrice = hasProductNetPrice(product);
                              const showProductQuantity = hasProductAmount(product);
                              const qtyForPrice = showProductQuantity ? Math.max(1, cartQuantity || 0) : 1;
                              const discountBadge = showProductPrice ? formatDiscountBadge(product) : null;
                              return (
                                <div key={productId} className="product-card">
                                  {discountBadge ? (
                                    <div className="product-discount-badge" aria-label={`Discount ${discountBadge}`}>
                                      {discountBadge}
                                    </div>
                                  ) : null}
                                  <div className="product-card-image">
                                    {product.image_url ? (
                                      <img
                                        src={resolveProductImageUrl(product.image_url)}
                                        alt={product.product_name}
                                        onError={(e) => {
                                          e.target.style.display = 'none';
                                        }}
                                      />
                                    ) : null}
                                  </div>
                                  <div className="product-info">
                                    <div className="product-name-header">
                                      <h3 className="product-name">{product.product_name}</h3>
                                    </div>
                                    {(() => {
                                      const productTypeDisplay = product.product_type || product.productType || (Array.isArray(product.product_types) && product.product_types?.[0]) || product.product_types;
                                      return productTypeDisplay ? (
                                        <div className="product-type-heading">Type: {productTypeDisplay}</div>
                                      ) : null;
                                    })()}
                                    {product.major_usp && (() => {
                                      const majorUspPlain = stripHtml(product.major_usp);
                                      return (
                                        <div className="product-major-usp-wrapper">
                                          <span className="product-card-usp-heading">Key benefit</span>
                                          <span className="product-major-usp-text">{majorUspPlain}</span>
                                        </div>
                                      );
                                    })()}
                                    {product.concerns_treated && product.concerns_treated.length > 0 && (
                                      <div className="product-info-section">
                                        <div className="product-info-label">
                                          <img
                                            src={`${process.env.PUBLIC_URL}/icon/treat.png`}
                                            alt="Treats"
                                            className="product-info-icon"
                                          />
                                          <span>Treats</span>
                                        </div>
                                        <div className="product-tags-container">
                                          {product.concerns_treated.map((concern, i) => (
                                            <span key={i} className="product-tag">{concern}</span>
                                          ))}
                                        </div>
                                      </div>
                                    )}

                                    {product.skin_goals_supported && product.skin_goals_supported.length > 0 && (
                                      <div className="product-info-section">
                                        <div className="product-info-label">
                                          <img
                                            src={`${process.env.PUBLIC_URL}/icon/goal.png`}
                                            alt="Goals"
                                            className="product-info-icon"
                                          />
                                          <span>Goals</span>
                                        </div>
                                        <div className="product-tags-container">
                                          {product.skin_goals_supported.map((goal, i) => (
                                            <span key={i} className="product-tag">{goal}</span>
                                          ))}
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                  <div className="product-card-footer">
                                    {showProductPrice ? (
                                      <div className="product-price-quantity">
                                        {(() => {
                                          const unitNet = toNumberOrNull(product.amount) ?? 0;
                                          const unitSale = toNumberOrNull(product.sale_price) ?? unitNet;
                                          const totalNet = unitNet * qtyForPrice;
                                          const totalSale = unitSale * qtyForPrice;
                                          if (hasProductDiscount(product) && totalSale > 0 && totalSale < totalNet) {
                                            return (
                                              <>
                                                <span className="product-price product-net-price-slash">{`₹${totalNet.toFixed(0)}`}</span>
                                                <span className="product-price product-sale-price">{`₹${totalSale.toFixed(0)}`}</span>
                                              </>
                                            );
                                          }
                                          return (
                                            <span className="product-price">{`₹${totalNet.toFixed(0)}`}</span>
                                          );
                                        })()}
                                      </div>
                                    ) : null}
                                    {showProductQuantity ? (
                                      <ProductCartCounter
                                        key={`counter-${productId}`}
                                        product={product}
                                        onAddToCart={(product, quantity) => handleAddToCart(product, quantity, productId)}
                                        disabled={!product.add_to_cart}
                                        initialQuantity={cartQuantity}
                                      />
                                    ) : (
                                      <button
                                        type="button"
                                        className={`skin-action-button add-to-cart-button ${cartQuantity > 0 ? 'request-selected' : ''}`}
                                        onClick={() => handleAddToCart(product, cartQuantity > 0 ? 0 : 1, productId)}
                                      >
                                        {cartQuantity > 0 ? 'Selected for Stock' : 'Add to Stock Request'}
                                      </button>
                                    )}
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        ) : (
                          <div className="no-products-message">
                            <p>No evening products found.</p>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
                  );
                })()}
              </>
            ) : null}
          </section>
        </div>
      </main>
      )}

      {/* USP Popup */}
      {uspPopup.open && (
        <div
          className="product-usp-popup-overlay"
          onClick={handleCloseUspPopup}
        >
          <div
            className="product-usp-popup"
            style={{
              top: `${uspPopup.position.top}px`,
              left: `${uspPopup.position.left}px`,
              width: `${Math.max(uspPopup.position.width, 300)}px`,
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="product-usp-popup-content">
              <button
                className="product-usp-popup-close"
                onClick={handleCloseUspPopup}
                aria-label="Close"
              >
                ×
              </button>
              {uspPopup.heading && <h3 className="product-usp-popup-heading">{uspPopup.heading}</h3>}
              {(() => {
                const raw = String(uspPopup.text);
                const isHtml = /<[a-z][\s\S]*>/i.test(raw);
                const html = isHtml ? sanitizeHtml(raw) : sanitizeHtml(raw.replace(/\r\n/g, '\n').replace(/\r/g, '\n').replace(/\n/g, '<br />'));
                return <div className="product-usp-popup-text" dangerouslySetInnerHTML={{ __html: html }} />;
              })()}
            </div>
          </div>
        </div>
      )}

      <PageFooter />

      {/* Checkout Modal (combined treatments + products) */}
      <CombinedCheckoutModal
        isOpen={isCheckoutModalOpen}
        onClose={handleCloseCheckoutModal}
        productCartItemsMap={cartItems}
        treatmentCartItemsMap={treatmentCartItems}
        onProductCartUpdate={handleCartUpdate}
        onTreatmentCartUpdate={handleTreatmentCartUpdate}
        sessionId={sessionId}
        apiBaseUrl={API_BASE_URL}
        token={token}
      />
    </div>
  );
};

export default ProductRecommendationsPage;

